// Parked bills and Orders, shown in place of the style grid.
// Core's ParkedBillsModal/OrderHistoryModal are not used here: CoreModals mounts
// them without an `open` prop, so on this layout they render invisibly but still
// evaluate their rows (and could never be closed). Everything below goes through
// the same Core handlers SuperMarket uses — retrieveParkedData, discardMessage,
// getSalesHistoryData, searchReturnHistory — so parking, approvals and stock
// checks behave the same. Orders builds its own salesHistory query so it opens on
// every till in the store (see utils/fashion/ordersQuery).
import React from "react";
import useTransactionStore from "../../../stores/useTransactionStore";
import { getWorkflowData } from "../../../utils/getWorkflowData";
import { returnAlreadyParked } from "../../../utils/fashion/returnLines";
import FashionReturnView from "./FashionReturnView";
import ParkedBillsView from "./bills/ParkedBillsView";
import OrdersView from "./bills/OrdersView";
import "./FashionBillsPanel.css";

// Order rows carry the customer as a name string, an object, or nothing.
export function customerLabel(customer) {
  if (!customer) return "Walk-in";
  if (typeof customer === "string") return customer || "Walk-in";
  return customer.name || "Walk-in";
}

// SuperMarket's "Sales Return" gate (SMPurchaseReturnView, BUG-1744): parked
// check, then the approval rule, then Core loads the bill for return.
export async function startOrderReturn(order, { cart, posConfig, parkedList, dispatchEvent, setCheckedKeys, searchReturnHistory }) {
  const billOpen = (cart?.items || []).some((i) => Number(i?.weight) !== 0) || cart?.isReturn === true || (cart?.payments || []).length > 0;
  if (billOpen) return { error: "Finish or park the current bill before starting a return." };
  if (returnAlreadyParked(order, parkedList, posConfig)) return { error: "A return of this bill is already parked. Retrieve it from Parked bills." };
  const proceed = async () => {
    setCheckedKeys?.([]);
    await searchReturnHistory?.(order.documentno, order);
    useTransactionStore.getState().setSalesReturn("Y");
    return { ok: true };
  };
  const { workflowRules, approvers } = await getWorkflowData();
  const rule = (workflowRules || []).find((r) => r.eventName === "Sales Return Process" || r.eventName === "Sales Return Approval");
  if (!rule || typeof dispatchEvent !== "function") return proceed();
  return new Promise((resolve) => {
    dispatchEvent(rule.eventName, rule.ruleName, rule.ruleCondition, approvers, null, (eventData) => {
      if (eventData?.approvalGranted) {
        useTransactionStore.getState().setSalesReturnApproved(true);
        resolve(proceed());
      } else {
        resolve({ error: "The return wasn't approved." });
      }
    });
  });
}

// Drops a return that was opened but never put on the bill, including its
// approval, so it can't come back later on top of a sale or carry the approval
// to another return.
export function abandonReturn({ setShowReturnPurchase, setCheckedKeys } = {}) {
  setShowReturnPurchase?.(false);
  setCheckedKeys?.([]);
  useTransactionStore.getState().setSalesReturn("N");
  useTransactionStore.getState().setSalesReturnApproved(false);
}

// "FCHE01-POS1 · " ahead of the parked bills subtitle, when the till has a name.
function tillName(tillData) {
  const t = tillData?.tillAccess?.cwrTill;
  const name = t?.till || t?.name || "";
  return name ? `${name} · ` : "";
}

export default function FashionBillsPanel({ panel, onClose, isOnline, backLabel = "Back to styles", ...props }) {
  const online = typeof isOnline === "boolean" ? isOnline : typeof navigator === "undefined" || navigator.onLine;
  const returning = panel === "orders" && Boolean(props.showReturnPurchase && props.selectedReturnOrder);
  const title = panel === "parked" ? "Parked bills" : returning ? "Return or exchange" : "Orders";
  const cancelReturn = () => abandonReturn(props);
  return (
    <section className={`fp-panel fp-bills${panel === "orders" && !returning ? " is-orders" : ""}`} aria-label={title}>
      <div className="fp-panel-head">
        <span className="fp-bills-titles">
          <h2 className="fp-panel-title">{title}</h2>
          {panel === "parked" && <span className="fp-bills-meta">{tillName(props.tillData)}bills stay here until retrieved or discarded</span>}
        </span>
        <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={returning ? cancelReturn : onClose}>
          {returning ? "Back to orders" : backLabel}
        </button>
      </div>
      {panel === "parked" ? (
        <ParkedBillsView {...props} online={online} floorBills retrieveTo="cart" customerLabel={customerLabel} onDone={onClose} />
      ) : returning ? (
        <FashionReturnView {...props} onCancel={cancelReturn} />
      ) : (
        <OrdersView {...props} online={online} startReturn={startOrderReturn} customerLabel={customerLabel} />
      )}
    </section>
  );
}
